import './AuditSchedulePicker.scss';
import { Form } from 'react-bootstrap';
import { Size } from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { SegmentedControlButton } from '../../Components/SegmentedControlButton';
import {
  AuditScheduleDraft,
  DAY_OF_WEEK_SHORT_LABELS,
  SCHEDULE_FREQUENCY_LABELS,
  SCHEDULE_TIME_OPTIONS,
  ScheduleFrequency,
  ScheduleFrequencyType,
  TIMEZONE_OPTIONS
} from './audit-config-constants';
import { todayIsoDate } from './audit-config-utils';

interface AuditSchedulePickerProps {
  error?: string;
  onChange: (schedule: AuditScheduleDraft) => void;
  value: AuditScheduleDraft;
}

const FREQUENCY_ITEMS = Object.values(ScheduleFrequency).map(frequency => ({
  label: SCHEDULE_FREQUENCY_LABELS[frequency],
  value: frequency
}));

const DAY_OPTIONS = DAY_OF_WEEK_SHORT_LABELS.map((label, day) => ({
  key: String(day),
  label
}));

const TIME_ITEMS = SCHEDULE_TIME_OPTIONS.map(time => ({ label: time, value: time }));

const isFrequency = (value: string): value is ScheduleFrequencyType =>
  Object.values<string>(ScheduleFrequency).includes(value);

export const AuditSchedulePicker = ({ error, onChange, value }: AuditSchedulePickerProps) => {
  const update = (patch: Partial<AuditScheduleDraft>) => onChange({ ...value, ...patch });

  const toggleDay = (key: string) => {
    const day = Number(key);
    update({
      daysOfWeek: value.daysOfWeek.includes(day)
        ? value.daysOfWeek.filter(d => d !== day)
        : [...value.daysOfWeek, day]
    });
  };

  const changeFrequency = (frequency: string) => {
    if (!isFrequency(frequency)) return;
    update({
      anchorDate: value.anchorDate || todayIsoDate(),
      dayOfMonth: value.dayOfMonth || '1',
      frequency,
      intervalDays: value.intervalDays || '1'
    });
  };

  // A saved schedule may use a zone outside the short list; keep it selectable.
  const timezoneItems = TIMEZONE_OPTIONS.some(option => option.value === value.timezone)
    ? TIMEZONE_OPTIONS
    : [
        ...TIMEZONE_OPTIONS,
        { label: value.timezone, shortLabel: value.timezone, value: value.timezone }
      ];

  return (
    <div className='audit-schedule-picker'>
      <div className='schedule-row'>
        <div className='schedule-cell'>
          <div className='cell-label'>Frequency</div>
          <Dropdown
            ariaLabel='Schedule frequency'
            options={FREQUENCY_ITEMS}
            value={value.frequency}
            onChange={changeFrequency}
            size={Size.MD}
          />
        </div>
        <div className='schedule-cell'>
          <div className='cell-label'>Time</div>
          <Dropdown
            ariaLabel='Schedule time'
            options={TIME_ITEMS}
            value={value.time}
            onChange={time => update({ time })}
            size={Size.MD}
          />
        </div>
        <div className='schedule-cell'>
          <div className='cell-label'>Timezone</div>
          <Dropdown
            ariaLabel='Schedule timezone'
            options={timezoneItems.map(({ label, value: zone }) => ({ label, value: zone }))}
            value={value.timezone}
            onChange={timezone => update({ timezone })}
            size={Size.MD}
          />
        </div>
      </div>
      {(value.frequency === ScheduleFrequency.WEEKLY ||
        value.frequency === ScheduleFrequency.BIWEEKLY) && (
        <div className='schedule-cell'>
          <div className='cell-label'>Days</div>
          <SegmentedControlButton
            isExclusiveSelect={false}
            options={DAY_OPTIONS}
            selectedOptions={value.daysOfWeek.map(String)}
            onChange={toggleDay}
            size={Size.MD}
          />
        </div>
      )}
      {value.frequency === ScheduleFrequency.MONTHLY && (
        <div className='schedule-row'>
          <div className='schedule-cell'>
            <div className='cell-label'>Day of the month</div>
            <Form.Control
              aria-label='Day of the month'
              inputMode='numeric'
              value={value.dayOfMonth}
              onChange={e => update({ dayOfMonth: e.target.value })}
            />
            <div className='cell-hint'>A shorter month runs on its last day.</div>
          </div>
        </div>
      )}
      {(value.frequency === ScheduleFrequency.BIWEEKLY ||
        value.frequency === ScheduleFrequency.EVERY_N_DAYS) && (
        <div className='schedule-row'>
          {value.frequency === ScheduleFrequency.EVERY_N_DAYS && (
            <div className='schedule-cell'>
              <div className='cell-label'>Every (days)</div>
              <Form.Control
                aria-label='Interval in days'
                inputMode='numeric'
                value={value.intervalDays}
                onChange={e => update({ intervalDays: e.target.value })}
              />
            </div>
          )}
          <div className='schedule-cell'>
            <div className='cell-label'>Starting on</div>
            <Form.Control
              aria-label='Anchor date'
              type='date'
              value={value.anchorDate}
              onChange={e => update({ anchorDate: e.target.value })}
            />
          </div>
        </div>
      )}
      {error && <div className='schedule-error'>{error}</div>}
    </div>
  );
};
